import { useEffect, useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";

const GRUPOS_PANEL = [
  { id: "kpis", titulo: "Indicadores (KPIs)" },
  { id: "bloques", titulo: "Bloques del panel" },
];

const GRUPOS_PLANIFICACION = [
  { id: "calendario", titulo: "Calendario" },
  { id: "vacaciones", titulo: "Vacaciones" },
  { id: "cobertura", titulo: "Cobertura" },
  { id: "ayuda", titulo: "Ayuda" },
];

function moverEnLista(lista, idx, dir) {
  const next = [...lista];
  const j = idx + dir;
  if (j < 0 || j >= next.length) return next;
  [next[idx], next[j]] = [next[j], next[idx]];
  return next;
}

export default function PanelConfigDialog({
  open,
  onClose,
  onSaved,
  configPath = "/dashboard/panel-config",
  title = "Personalizar panel",
  description = "Elige qué información quieres ver al entrar en el panel. La configuración es personal y solo afecta a tu usuario.",
  grupos = GRUPOS_PANEL,
  testIdPrefix = "panel",
}) {
  const [widgets, setWidgets] = useState([]);
  const [orden, setOrden] = useState([]);
  const [visible, setVisible] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    if (!open) return;
    setCargando(true);
    api
      .get(configPath)
      .then(({ data }) => {
        setWidgets(data.widgets || []);
        setOrden(data.orden || []);
        setVisible(data.visible || []);
      })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setCargando(false));
  }, [open, configPath]);

  const toggleVisible = (id, checked) => {
    setVisible((prev) => (checked ? [...prev, id] : prev.filter((w) => w !== id)));
  };

  const mover = (grupoId, idx, dir) => {
    const idsGrupo = orden.filter((id) => widgets.find((w) => w.id === id)?.grupo === grupoId);
    const id = idsGrupo[idx];
    const globalIdx = orden.indexOf(id);
    setOrden(moverEnLista(orden, globalIdx, dir));
  };

  const restaurar = () => {
    const defs = widgets.map((w) => w.id);
    setOrden(defs);
    setVisible(defs);
  };

  const guardar = async () => {
    if (visible.length === 0) {
      toast.error("Selecciona al menos un elemento para mostrar");
      return;
    }
    setGuardando(true);
    try {
      const { data } = await api.put(configPath, { visible, orden });
      onSaved?.(data);
      toast.success("Configuración guardada");
      onClose();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid={`${testIdPrefix}-config-dialog`}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-zinc-600">{description}</p>

        {cargando ? (
          <div className="flex justify-center py-8">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-zinc-200 border-t-zinc-950" />
          </div>
        ) : (
          <div className="max-h-[min(60vh,420px)] space-y-5 overflow-y-auto pr-1">
            {grupos.map((grupo) => {
              const items = orden
                .map((id) => widgets.find((w) => w.id === id))
                .filter((w) => w && w.grupo === grupo.id);
              if (items.length === 0) return null;
              return (
                <div key={grupo.id} className="space-y-2">
                  <p className="text-xs font-semibold uppercase tracking-wide text-zinc-500">{grupo.titulo}</p>
                  <ul className="space-y-2">
                    {items.map((w, idx) => (
                      <li
                        key={w.id}
                        className="flex items-start gap-3 rounded-lg border border-zinc-200 bg-zinc-50/80 px-3 py-2.5"
                      >
                        <Checkbox
                          id={`${testIdPrefix}-widget-${w.id}`}
                          checked={visible.includes(w.id)}
                          onCheckedChange={(v) => toggleVisible(w.id, v === true)}
                          className="mt-0.5"
                        />
                        <div className="min-w-0 flex-1">
                          <Label htmlFor={`${testIdPrefix}-widget-${w.id}`} className="cursor-pointer font-medium text-zinc-900">
                            {w.label}
                          </Label>
                          <p className="text-xs text-zinc-500">{w.descripcion}</p>
                        </div>
                        <div className="flex shrink-0 flex-col gap-0.5">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            disabled={idx === 0}
                            onClick={() => mover(grupo.id, idx, -1)}
                            aria-label="Subir"
                          >
                            <ChevronUp className="h-4 w-4" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            disabled={idx === items.length - 1}
                            onClick={() => mover(grupo.id, idx, 1)}
                            aria-label="Bajar"
                          >
                            <ChevronDown className="h-4 w-4" />
                          </Button>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        )}

        <DialogFooter className="flex-col gap-2 sm:flex-row sm:justify-between">
          <Button type="button" variant="ghost" onClick={restaurar} disabled={cargando || guardando}>
            Restaurar todo
          </Button>
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={guardando}>
              Cancelar
            </Button>
            <Button type="button" onClick={guardar} disabled={cargando || guardando} data-testid={`${testIdPrefix}-config-guardar`}>
              {guardando ? "Guardando…" : "Guardar"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export { GRUPOS_PLANIFICACION };
